import React from 'react';
import { createMRTColumnHelper, type MRT_ColumnDef } from 'material-react-table';
import { ProviderBadge } from '../../components/ProviderBadge';

export interface AflDailyRow {
  Date: string;
  Store?: string;
  'RO Number': string;
  'ADV #'?: string;
  'ADV NAME'?: string;
  'Last Name': string;
  'First Name'?: string;
  'Home Phone'?: string;
  'Business Phone'?: string;
  VIN: string;
  Year?: string | number;
  Make?: string;
  Model?: string;
  Mileage?: string | number;
  'Address 1'?: string;
  'Address 2'?: string;
  City?: string;
  State?: string;
  'Zip Code'?: string;
  'Op Code'?: string;
  'Line Number'?: string | number;
  Category?: string;
  'Line Type'?: string;
  'Line Pay Method'?: string;
  'Service Type'?: string;
  'Labor Amount'?: number | string;
  Comment?: string;
  'Part Number'?: string;
  TansGroup?: string;
  TransCode?: string;
  'Special Order'?: string;
  Manufacturer?: string;
  Description?: string;
  'Net Price'?: number | string;
  'Serv Sales': number;
  'Serv Prem'?: number;
  'Parts Sales': number;
  'Parts Premium': number;
  'OEM Covered'?: boolean | null;
  'Desc Covered'?: boolean | null;
  coveredLine?: boolean;
  coveredPart?: boolean;
  'T1 Label'?: string;
  'Type Code'?: string;
  'Provider Source'?: string;
  providerSource?: string;
}

export const PII_COLUMN_KEYS: Array<keyof AflDailyRow> = [
  // NOTE: First Name and Last Name are kept visible per user requirements
  // All other PII fields are hidden by default when privacy mode is enabled
  'Home Phone',
  'Business Phone',
  'Address 1',
  'Address 2',
  'City',
  'State',
  'Zip Code',
  'VIN',
  'ADV NAME',
];

type ListFilterConfig = {
  type: 'list';
  param: string;
  normalize?: (values: string[]) => string[];
};

type TextFilterConfig = {
  type: 'text';
  param: string;
  normalize?: (value: string) => string;
};

export type ColumnFilterParamConfig = ListFilterConfig | TextFilterConfig;

const TYPE_CODE_WHITELIST = new Set(['C', 'I', '0']);
const T1_LABEL_WHITELIST = new Set(['business', 'individual', 'internal', 'voided']);

const normalizeList = (values: string[]) =>
  Array.from(
    new Set(
      values.map((value) => (value == null ? '' : String(value))).map((value) => value.trim()),
    ),
  );

const normalizeTypeCodes = (values: string[]) => {
  const normalized = normalizeList(values).map((value) => value.toUpperCase());
  return normalized.filter((value) => value === '' || TYPE_CODE_WHITELIST.has(value));
};

const normalizeT1Labels = (values: string[]) => {
  const normalized = normalizeList(values).map((value) => value.toLowerCase());
  return normalized.filter((value) => value === '' || T1_LABEL_WHITELIST.has(value));
};

const PROVIDER_WHITELIST = new Set(['opentrack', 'dealer_vault']);
const normalizeProviderSources = (values: string[]) => {
  const normalized = normalizeList(values).map((value) => value.toLowerCase());
  return normalized.filter((value) => value === '' || PROVIDER_WHITELIST.has(value));
};

const normalizeCoverageValues = (values: string[]): string[] => {
  const result: string[] = [];
  for (const value of normalizeList(values)) {
    const lower = value.toLowerCase();
    if (lower === '1' || lower === 'true') {
      result.push('true');
    } else if (lower === '0' || lower === 'false') {
      result.push('false');
    } else if (lower === '') {
      result.push('');
    }
  }
  return result;
};

const normalizeText = (value: string) => value.trim();
const normalizeUpperText = (value: string) => value.trim().toUpperCase();

export const COLUMN_FILTER_CONFIG: Record<string, ColumnFilterParamConfig> = {
  // Discrete include-list filters
  'Line Type': { type: 'list', param: 'lineType', normalize: normalizeList },
  'Line Pay Method': { type: 'list', param: 'linePay', normalize: normalizeList },
  'Service Type': { type: 'list', param: 'serviceType', normalize: normalizeList },
  Category: { type: 'list', param: 'category', normalize: normalizeList },
  Manufacturer: { type: 'list', param: 'manufacturer', normalize: normalizeList },
  'Special Order': { type: 'list', param: 'specialOrder', normalize: normalizeList },
  TransCode: { type: 'list', param: 'transCode', normalize: normalizeList },
  TansGroup: { type: 'list', param: 'tansGroup', normalize: normalizeList },
  'Op Code': { type: 'list', param: 'opCode', normalize: normalizeList },
  Store: { type: 'list', param: 'store', normalize: normalizeList },
  'Provider Source': { type: 'list', param: 'providerSource', normalize: normalizeProviderSources },
  coveredLine: { type: 'list', param: 'coveredLine', normalize: normalizeCoverageValues },
  coveredPart: { type: 'list', param: 'coveredPart', normalize: normalizeCoverageValues },
  'Type Code': { type: 'list', param: 'typeCode', normalize: normalizeTypeCodes },
  'T1 Label': { type: 'list', param: 't1Label', normalize: normalizeT1Labels },

  // Keyword/contains filters
  'RO Number': { type: 'text', param: 'roNumberContains', normalize: normalizeText },
  VIN: { type: 'text', param: 'vinContains', normalize: normalizeUpperText },
  'Line Number': { type: 'text', param: 'lineNumberContains', normalize: normalizeText },
  'Last Name': { type: 'text', param: 'lastNameContains', normalize: normalizeText },
  'First Name': { type: 'text', param: 'firstNameContains', normalize: normalizeText },
  Description: { type: 'text', param: 'descriptionContains', normalize: normalizeText },
  Comment: { type: 'text', param: 'commentContains', normalize: normalizeText },
  'Part Number': { type: 'text', param: 'partNumberContains', normalize: normalizeUpperText },
  'ADV NAME': { type: 'text', param: 'advNameContains', normalize: normalizeText },
  City: { type: 'text', param: 'cityContains', normalize: normalizeText },
  State: { type: 'text', param: 'stateContains', normalize: normalizeUpperText },
  'Zip Code': { type: 'text', param: 'zipCodeContains', normalize: normalizeText },
};

export const MAIN_REPORT_ALLOWED_COLUMN_KEYS: Array<keyof AflDailyRow> = [
  'Date',
  'RO Number',
  'Last Name',
  'Line Number',
  'Type Code',
  'Line Type',
  'Line Pay Method',
  'Labor Amount',
  'Part Number',
  'Special Order',
  'Manufacturer',
  'TransCode',
  'Description',
  'coveredLine',
  'coveredPart',
  'OEM Covered',
  'Desc Covered',
  'providerSource',
  'Serv Sales',
  'Serv Prem',
  'Parts Sales',
  'Parts Premium',
];

const DEFAULT_VISIBLE_COLUMN_KEYS: Array<keyof AflDailyRow> = [...MAIN_REPORT_ALLOWED_COLUMN_KEYS];

// Columns that should be visible in the show/hide menu
// All other columns will be hidden from the menu entirely
const VISIBLE_IN_MENU_COLUMN_KEYS: Array<keyof AflDailyRow> = [...MAIN_REPORT_ALLOWED_COLUMN_KEYS];

const c = createMRTColumnHelper<AflDailyRow>();

// Render USD currency for monetary values; show blank when value is zero
const usdFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' });
function renderCurrencyBlankIfZero({ cell }: { cell: any }) {
  const raw = cell.getValue() as unknown;
  if (raw === null || raw === undefined || raw === '') return '';
  let num: number | null = null;
  if (typeof raw === 'number') {
    num = raw;
  } else if (typeof raw === 'string') {
    const parsed = parseFloat(raw.replace(/[, ]/g, ''));
    num = Number.isFinite(parsed) ? parsed : null;
  }
  if (num === null) return '';
  if (num === 0) return '';
  return usdFormatter.format(num);
}

const rawColumns: MRT_ColumnDef<AflDailyRow, any>[] = [
  // 1. Date & Store
  c.accessor('Date', { header: 'Date', size: 95, minSize: 85, maxSize: 120, enableSorting: true }),
  c.accessor('Store', {
    header: 'Store',
    size: 75,
    minSize: 65,
    maxSize: 100,
    enableSorting: true,
    filterVariant: 'multi-select',
  }),

  // 2. RO & Advisor info
  c.accessor('RO Number', {
    header: 'RO#',
    size: 80,
    minSize: 70,
    maxSize: 110,
    enableSorting: true,
  }),
  c.accessor('ADV #', { header: 'ADV #', size: 60, minSize: 50, maxSize: 80, enableSorting: true }),
  c.accessor('ADV NAME', {
    header: 'ADV NAME',
    size: 100,
    minSize: 80,
    maxSize: 150,
    enableSorting: true,
  }),

  // 3. Customer info (Name & Phone)
  c.accessor('Last Name', {
    header: 'Last Name',
    enableSorting: true,
    size: 90,
    minSize: 70,
    maxSize: 130,
  }),
  c.accessor('First Name', {
    header: 'First Name',
    size: 90,
    minSize: 70,
    maxSize: 130,
    enableSorting: true,
  }),
  c.accessor('Home Phone', { header: 'Home Phone', size: 95, minSize: 80, maxSize: 120 }),
  c.accessor('Business Phone', { header: 'Business Phone', size: 95, minSize: 80, maxSize: 120 }),

  // 4. Vehicle info
  c.accessor('VIN', { header: 'VIN', size: 110, minSize: 100, maxSize: 150, enableSorting: true }),
  c.accessor('Year', { header: 'Year', size: 50, minSize: 45, maxSize: 70, enableSorting: true }),
  c.accessor('Make', { header: 'Make', size: 70, minSize: 60, maxSize: 100, enableSorting: true }),
  c.accessor('Model', {
    header: 'Model',
    size: 85,
    minSize: 70,
    maxSize: 120,
    enableSorting: true,
  }),
  c.accessor('Mileage', {
    header: 'Mileage',
    size: 70,
    minSize: 60,
    maxSize: 100,
    enableSorting: true,
  }),

  // 5. Address info
  c.accessor('Address 1', { header: 'Address 1', size: 140, minSize: 100, maxSize: 200 }),
  c.accessor('Address 2', { header: 'Address 2', size: 140, minSize: 100, maxSize: 200 }),
  c.accessor('City', { header: 'City', size: 90, minSize: 70, maxSize: 140, enableSorting: true }),
  c.accessor('State', { header: 'State', size: 50, minSize: 45, maxSize: 70, enableSorting: true }),
  c.accessor('Zip Code', {
    header: 'Zip Code',
    size: 75,
    minSize: 65,
    maxSize: 100,
    enableSorting: true,
  }),

  // 6. Line identification & category
  c.accessor('Op Code', {
    header: 'Op Code',
    size: 70,
    minSize: 60,
    maxSize: 100,
    enableSorting: true,
    filterVariant: 'multi-select',
  }),
  c.accessor('Line Number', {
    header: 'Line',
    size: 60,
    minSize: 55,
    maxSize: 80,
    enableSorting: true,
  }),
  c.accessor('Category', {
    header: 'Category',
    size: 90,
    minSize: 80,
    maxSize: 120,
    enableSorting: true,
    filterVariant: 'multi-select',
  }),
  c.accessor('Line Type', {
    header: 'Line Type',
    size: 75,
    minSize: 70,
    maxSize: 95,
    enableSorting: true,
    filterVariant: 'multi-select',
    filterSelectOptions: [
      { label: 'A', value: 'A' },
      { label: 'L', value: 'L' },
      { label: '(blank)', value: '' },
      { label: 'I', value: 'I' },
      { label: 'W', value: 'W' },
      { label: 'MR', value: 'MR' },
    ],
  }),
  c.accessor('Line Pay Method', {
    header: 'Pay Method',
    size: 80,
    minSize: 75,
    maxSize: 105,
    enableSorting: true,
    filterVariant: 'multi-select',
  }),
  c.accessor('Service Type', {
    header: 'Svc Type',
    size: 75,
    minSize: 70,
    maxSize: 100,
    enableSorting: true,
    filterVariant: 'multi-select',
  }),

  // 7. Labor & Comments
  c.accessor('Labor Amount', {
    header: 'Labor Amt',
    size: 80,
    minSize: 70,
    maxSize: 110,
    enableSorting: true,
    Cell: renderCurrencyBlankIfZero,
  }),
  c.accessor('Comment', { header: 'Comment', size: 120, minSize: 100, maxSize: 200 }),

  // 8. Parts identification
  c.accessor('Part Number', {
    header: 'Part Number',
    size: 90,
    minSize: 75,
    maxSize: 120,
    enableSorting: true,
  }),
  c.accessor('TansGroup', {
    header: 'Trans Group',
    size: 80,
    minSize: 75,
    maxSize: 105,
    enableSorting: true,
    filterVariant: 'multi-select',
  }),
  c.accessor('TransCode', {
    header: 'Trans Code',
    size: 80,
    minSize: 75,
    maxSize: 105,
    enableSorting: true,
    filterVariant: 'multi-select',
  }),
  c.accessor('Special Order', {
    header: 'Special Order',
    size: 95,
    minSize: 85,
    maxSize: 125,
    enableSorting: true,
    filterVariant: 'multi-select',
  }),
  c.accessor('Manufacturer', {
    header: 'Manu.',
    size: 70,
    minSize: 65,
    maxSize: 95,
    enableSorting: true,
    filterVariant: 'multi-select',
  }),
  c.accessor('Description', {
    header: 'Desc',
    size: 110,
    minSize: 90,
    maxSize: 180,
    enableSorting: true,
  }),

  // 9. Financial columns
  c.accessor('Net Price', {
    header: 'Net Price',
    size: 80,
    minSize: 70,
    maxSize: 110,
    enableSorting: true,
    Cell: renderCurrencyBlankIfZero,
  }),
  c.accessor('Serv Sales', {
    header: 'Serv Sales',
    size: 80,
    minSize: 70,
    maxSize: 110,
    enableSorting: true,
    Cell: renderCurrencyBlankIfZero,
  }),
  c.accessor('Serv Prem', {
    header: 'Serv Prem',
    size: 80,
    minSize: 70,
    maxSize: 110,
    enableSorting: true,
    Cell: renderCurrencyBlankIfZero,
  }),
  c.accessor('Parts Sales', {
    header: 'Parts Sales',
    size: 80,
    minSize: 70,
    maxSize: 110,
    enableSorting: true,
    Cell: renderCurrencyBlankIfZero,
  }),
  c.accessor('Parts Premium', {
    header: 'Parts Prem',
    size: 80,
    minSize: 70,
    maxSize: 110,
    enableSorting: true,
    Cell: renderCurrencyBlankIfZero,
  }),

  // 10. Classification & Coverage columns
  c.accessor('coveredLine', {
    header: 'Cov\nLine',
    size: 45,
    minSize: 40,
    maxSize: 65,
    grow: false,
    enableSorting: true,
    filterVariant: 'multi-select',
    Cell: ({ cell }) => (cell.getValue() ? 'Yes' : ''),
  }),
  c.accessor('coveredPart', {
    header: 'Cov\nPart',
    size: 45,
    minSize: 40,
    maxSize: 65,
    grow: false,
    enableSorting: true,
    filterVariant: 'multi-select',
    Cell: ({ cell }) => (cell.getValue() ? 'Yes' : ''),
  }),
  c.accessor('OEM Covered', {
    header: '#',
    size: 45,
    minSize: 40,
    maxSize: 65,
    grow: false,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ cell }) => {
      const val = cell.getValue<boolean | null>();
      if (val == null) return <span style={{ color: '#999' }}>–</span>;
      return val ? (
        <span style={{ color: 'green' }}>✓</span>
      ) : (
        <span style={{ color: 'red' }}>✗</span>
      );
    },
  }),
  c.accessor('Desc Covered', {
    header: 'Desc',
    size: 50,
    minSize: 40,
    maxSize: 70,
    grow: false,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ cell }) => {
      const val = cell.getValue<boolean | null>();
      if (val == null) return <span style={{ color: '#999' }}>–</span>;
      return val ? (
        <span style={{ color: 'green' }}>✓</span>
      ) : (
        <span style={{ color: 'red' }}>✗</span>
      );
    },
  }),
  c.accessor('Type Code', {
    header: 'Type\nCode',
    size: 50,
    minSize: 45,
    maxSize: 75,
    grow: false,
    enableSorting: true,
    filterVariant: 'multi-select',
    filterSelectOptions: [
      { label: 'Company (C)', value: 'C' },
      { label: 'Individual (I)', value: 'I' },
      { label: 'Unknown (0)', value: '0' },
      { label: '(blank)', value: '' },
    ],
  }),
  c.accessor('T1 Label', {
    header: 'Tier 1',
    size: 70,
    minSize: 60,
    maxSize: 100,
    enableSorting: true,
    filterVariant: 'multi-select',
    filterSelectOptions: [
      { label: 'Business', value: 'business' },
      { label: 'Individual', value: 'individual' },
      { label: 'Internal', value: 'internal' },
      { label: 'Voided', value: 'voided' },
      { label: '(blank)', value: '' },
    ],
  }),

  // 11. Provider Source (at end)
  {
    accessorKey: 'providerSource',
    header: 'Source',
    size: 75,
    minSize: 65,
    maxSize: 110,
    enableSorting: true,
    accessorFn: (row: AflDailyRow) => row.providerSource ?? (row as any)['Provider Source'] ?? '',
    filterVariant: 'multi-select' as const,
    filterSelectOptions: [
      { label: 'OpenTrack', value: 'opentrack' },
      // { label: 'DealerVault', value: 'dealer_vault' },
    ],
    Cell: ({ cell }: { cell: any }) => {
      const provider = cell.getValue() as string | null | undefined;
      return <ProviderBadge provider={provider ?? null} size="small" />;
    },
  },
];

function applyDefaultFilterDefaults(
  col: MRT_ColumnDef<AflDailyRow, any>,
): MRT_ColumnDef<AflDailyRow, any> {
  const key = String((col as any).accessorKey ?? (col as any).id ?? '');
  if (!key) return col;

  const next: MRT_ColumnDef<AflDailyRow, any> = { ...col };

  // Only show specific columns in the show/hide menu
  // All other columns will be hidden from the menu
  const visibleInMenuSet = new Set(VISIBLE_IN_MENU_COLUMN_KEYS.map(String));
  if (!visibleInMenuSet.has(key)) {
    next.visibleInShowHideMenu = false;
  }

  const config = COLUMN_FILTER_CONFIG[key];
  if (!config) return next;

  if (next.enableColumnFilter === undefined) {
    next.enableColumnFilter = true;
  }
  if (config.type === 'list' && next.filterVariant === undefined) {
    next.filterVariant = 'multi-select';
  } else if (config.type === 'text' && next.filterVariant === undefined) {
    next.filterVariant = 'text';
    // For server-side filtering, we need to disable client-side filtering
    // by providing a no-op filterFn that always returns true
    if (next.filterFn === undefined) {
      next.filterFn = () => true;
    }
  }
  return next;
}

export const columns: MRT_ColumnDef<AflDailyRow, any>[] = rawColumns.map(
  applyDefaultFilterDefaults,
);

const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = (() => {
  const visibleDefaults = new Set(DEFAULT_VISIBLE_COLUMN_KEYS.map(String));
  const visibility: Record<string, boolean> = {};
  for (const col of columns) {
    const columnId = String((col as any).accessorKey ?? (col as any).id ?? '');
    if (!columnId) continue;
    if (!visibleDefaults.has(columnId)) {
      visibility[columnId] = false;
    }
  }
  return visibility;
})();

export function getInitialColumnVisibility(privacyEnabled: boolean): Record<string, boolean> {
  const visibility: Record<string, boolean> = { ...DEFAULT_COLUMN_VISIBILITY };

  // Explicitly hide columns not needed for current reporting
  const HIDDEN_COLUMNS: Array<keyof AflDailyRow> = [
    'Store',
    'ADV #',
    'Year',
    'Make',
    'Model',
    'Mileage',
    'Net Price',
    'TansGroup',
    'Comment',
    // Hide all PII columns that have no data for OpenTrack
    'Home Phone',
    'Business Phone',
    'Address 1',
    'Address 2',
    'City',
    'State',
    'Zip Code',
    'First Name', // Empty in OpenTrack default view
  ];

  for (const key of HIDDEN_COLUMNS) {
    visibility[String(key)] = false;
  }

  if (privacyEnabled) {
    for (const key of PII_COLUMN_KEYS) {
      visibility[String(key)] = false;
    }
  }

  return visibility;
}

export function sanitizeColumnVisibility(
  visibility: Record<string, boolean>,
): Record<string, boolean> {
  const allowed = new Set(MAIN_REPORT_ALLOWED_COLUMN_KEYS.map(String));
  const next: Record<string, boolean> = { ...visibility };
  for (const key of Object.keys(next)) {
    if (!allowed.has(key)) next[key] = false;
  }
  return next;
}

export function getDefaultColumnOrder(): string[] {
  return [
    'Date', 'RO Number', 'Last Name', 'Line Number',
    'Type Code', 'Line Type', 'Line Pay Method', 'providerSource',
    'Labor Amount', 'Op Code', 'Service Type',
    'Part Number', 'Special Order', 'Manufacturer', 'TransCode', 'Description',
    'coveredLine', 'coveredPart', 'OEM Covered', 'Desc Covered',
    'Serv Sales', 'Serv Prem', 'Parts Sales', 'Parts Premium',
  ];
}

export function sortIdToApiField(id: string): string {
  const cleaned = String(id ?? '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) {
    return '';
  }

  const specialCases: Record<string, string> = {
    Date: 'close_date',
    'Net Price': 'unit_price',
  };

  if (specialCases[cleaned]) {
    return specialCases[cleaned];
  }

  const snake = cleaned
    .replace(/[^\w]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();

  if (snake && specialCases[snake]) {
    return specialCases[snake];
  }

  return snake || cleaned;
}
